import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

// Fallback to localStorage for web
const useCapacitor = Capacitor.isNativePlatform();

export const LocalStorage = {
  async setItem(key: string, value: any): Promise<void> {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    
    if (useCapacitor) {
      await Preferences.set({ key, value: stringValue });
    } else {
      localStorage.setItem(key, stringValue);
    }
  },

  async getItem(key: string): Promise<string | null> {
    if (useCapacitor) {
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      return localStorage.getItem(key);
    }
  },

  async getItemParsed<T>(key: string): Promise<T | null> {
    const value = await this.getItem(key);
    if (!value) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  },

  async removeItem(key: string): Promise<void> {
    if (useCapacitor) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  },

  async clear(): Promise<void> {
    if (useCapacitor) {
      await Preferences.clear();
    } else {
      localStorage.clear();
    }
  },

  async keys(): Promise<string[]> {
    if (useCapacitor) {
      const { keys } = await Preferences.keys();
      return keys;
    } else {
      return Object.keys(localStorage);
    }
  },
};

// Transaction storage
export interface StoredTransaction {
  id: string;
  transaction_id: string;
  amount_sent: number;
  amount_received: number;
  direction: string;
  exchange_rate: number;
  fee_amount: number;
  margin_percentage: number;
  source_account: string;
  destination_account: string;
  notes: string | null;
  created_at: string;
}

export interface SavedRate {
  direction: string;
  buyer_rate: number;
  seller_rate: number;
  created_at?: string;
}

export interface SavedRecipient {
  id: string;
  name: string;
  phone: string;
  note?: string;
  created_at: string;
}

// Transaction operations
export const TransactionStorage = {
  async getAllTransactions(): Promise<StoredTransaction[]> {
    const data = await LocalStorage.getItemParsed<StoredTransaction[]>('transactions');
    return data || [];
  },

  async addTransaction(transaction: StoredTransaction): Promise<void> {
    const transactions = await this.getAllTransactions();
    transactions.unshift(transaction);
    await LocalStorage.setItem('transactions', transactions);
  },

  async deleteTransaction(transactionId: string): Promise<void> {
    const transactions = await this.getAllTransactions();
    const filtered = transactions.filter(t => t.transaction_id !== transactionId);
    await LocalStorage.setItem('transactions', filtered);
  },

  async getTransactionById(transactionId: string): Promise<StoredTransaction | null> {
    const transactions = await this.getAllTransactions();
    return transactions.find(t => t.transaction_id === transactionId) || null;
  },
};

// Rate operations
export const RateStorage = {
  async getSavedRates(): Promise<{ KWD_GHS?: SavedRate; GHS_KWD?: SavedRate }> {
    const data = await LocalStorage.getItemParsed<{ KWD_GHS?: SavedRate; GHS_KWD?: SavedRate }>('rates');
    return data || {};
  },

  async saveRate(direction: string, buyerRate: number, sellerRate: number): Promise<void> {
    const rates = await this.getSavedRates();
    rates[direction as keyof typeof rates] = {
      direction,
      buyer_rate: buyerRate,
      seller_rate: sellerRate,
      created_at: new Date().toISOString(),
    };
    await LocalStorage.setItem('rates', rates);
  },

  async getRate(direction: string): Promise<SavedRate | null> {
    const rates = await this.getSavedRates();
    return rates[direction as keyof typeof rates] || null;
  },
};

// Recipient operations
export const RecipientStorage = {
  async getAllRecipients(): Promise<SavedRecipient[]> {
    const data = await LocalStorage.getItemParsed<SavedRecipient[]>('recipients');
    return data || [];
  },

  async addRecipient(recipient: SavedRecipient): Promise<void> {
    const recipients = await this.getAllRecipients();
    recipients.push(recipient);
    await LocalStorage.setItem('recipients', recipients);
  },

  async deleteRecipient(recipientId: string): Promise<void> {
    const recipients = await this.getAllRecipients();
    const filtered = recipients.filter(r => r.id !== recipientId);
    await LocalStorage.setItem('recipients', filtered);
  },

  async updateRecipient(recipientId: string, updates: Partial<SavedRecipient>): Promise<void> {
    const recipients = await this.getAllRecipients();
    const index = recipients.findIndex(r => r.id === recipientId);
    if (index !== -1) {
      recipients[index] = { ...recipients[index], ...updates };
      await LocalStorage.setItem('recipients', recipients);
    }
  },

  async getRecipientById(recipientId: string): Promise<SavedRecipient | null> {
    const recipients = await this.getAllRecipients();
    return recipients.find(r => r.id === recipientId) || null;
  },
};

// App settings
export const SettingsStorage = {
  async getSetting(key: string): Promise<any> {
    return await LocalStorage.getItem(`setting_${key}`);
  },

  async setSetting(key: string, value: any): Promise<void> {
    await LocalStorage.setItem(`setting_${key}`, value);
  },

  async getServiceChargeEnabled(): Promise<boolean> {
    const value = await this.getSetting('serviceChargeEnabled');
    return value === 'true';
  },

  async setServiceChargeEnabled(enabled: boolean): Promise<void> {
    await this.setSetting('serviceChargeEnabled', enabled.toString());
  },
};
